import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * SkyCycle — Optimized Animated day/night backdrop
 *
 * Performance optimizations:
 * - Memoized star positions to avoid random generation each render
 * - CSS transforms instead of top/left for better performance  
 * - Respects prefers-reduced-motion
 * - Cached animation calculations
 * - Proper ARIA attributes for accessibility
 */

// Phase definition
const PHASES = [
  {
    key: "night",
    label: "Nuit",
    startHour: 0,
    endHour: 4,
    bg: ["#050914", "#0b1736"], // deep navy
    starOpacity: 1,
  },
  {
    key: "preDawn",
    label: "Aube",
    startHour: 4,
    endHour: 6,
    bg: ["#1a1440", "#6b3e7c"], // indigo → mauve
    starOpacity: 0.6,
  },
  {
    key: "sunrise",
    label: "Lever",
    startHour: 6,
    endHour: 8,
    bg: ["#ff9966", "#ff5e62"], // warm sunrise
    starOpacity: 0.2,
  },
  {
    key: "morning",
    label: "Matin",
    startHour: 8,
    endHour: 11,
    bg: ["#7ec8ff", "#a6e1ff"], // fresh blue
    starOpacity: 0,
  },
  {
    key: "noon",
    label: "Midi",
    startHour: 11,
    endHour: 14,
    bg: ["#4aa8ff", "#b3e5ff"], // bright blue
    starOpacity: 0,
  },
  {
    key: "afternoon",
    label: "Après-midi",
    startHour: 14,
    endHour: 18,
    bg: ["#64b5f6", "#ffd1a1"], // soft warm tint
    starOpacity: 0,
  },
  {
    key: "sunset",
    label: "Coucher",
    startHour: 18,
    endHour: 20,
    bg: ["#fe8c00", "#f83600"], // sunset orange → red
    starOpacity: 0.2,
  },
  {
    key: "dusk",
    label: "Crépuscule",
    startHour: 20,
    endHour: 22,
    bg: ["#2c3e50", "#4b0082"], // blue → indigo
    starOpacity: 0.7,
  },
  {
    key: "midnight",
    label: "Nuit Profonde",
    startHour: 22,
    endHour: 24,
    bg: ["#04070f", "#0b122b"],
    starOpacity: 1,
  },
];

function getNowFraction(date = new Date()) {
  const h = date.getHours();
  const m = date.getMinutes();
  const s = date.getSeconds();
  return (h * 3600 + m * 60 + s) / 86400; // 0..1
}

function fractionToHour(frac) {
  return frac * 24; // 0..24
}

function findPhaseByHour(hour) {
  return PHASES.find((p) => hour >= p.startHour && hour < p.endHour) || PHASES[0];
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Interpolate 2-color gradient between phases
function mixGradients(gradA, gradB, t) {
  const toRGB = (hex) => {
    const n = hex.replace("#", "");
    return [
      parseInt(n.slice(0, 2), 16),
      parseInt(n.slice(2, 4), 16),
      parseInt(n.slice(4, 6), 16),
    ];
  };
  const [a1, a2] = gradA.map(toRGB);
  const [b1, b2] = gradB.map(toRGB);
  const m1 = a1.map((v, i) => Math.round(lerp(v, b1[i], t)));
  const m2 = a2.map((v, i) => Math.round(lerp(v, b2[i], t)));
  const hex = (rgb) => `#${rgb.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  return [hex(m1), hex(m2)];
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

/** Compute sky state for a given fraction of the day (0..1). */
function useSkyState(dayFrac) {
  return useMemo(() => {
    // Determine current phase
    const hour = fractionToHour(dayFrac);
    const phase = findPhaseByHour(hour);

    // Neighbor phase for blending within the 2-3h windows
    const phaseIndex = PHASES.indexOf(phase);
    const phaseSpan = phase.endHour - phase.startHour;
    const phaseT = clamp((hour - phase.startHour) / Math.max(phaseSpan, 0.0001), 0, 1);

    // Blend to next phase's colors smoothly
    const next = PHASES[(phaseIndex + 1) % PHASES.length];
    const [c1, c2] = mixGradients(phase.bg, next.bg, phaseT);

    // Sun & Moon positions along arcs using transforms for better performance
    const TWO_PI = Math.PI * 2;
    const sunAngle = (dayFrac * TWO_PI) - Math.PI / 2; // -90° at 0.0
    const sunX = Math.cos(sunAngle) * 50; // -50..+50 vw percents
    const sunY = Math.sin(sunAngle) * -35; // negative goes up (vh percents)

    // Moon opposite the sun (180° out of phase)
    const moonAngle = sunAngle + Math.PI;
    const moonX = Math.cos(moonAngle) * 50;
    const moonY = Math.sin(moonAngle) * -35;

    // Stars visibility from phases (blend current and next)
    const starOpacity = lerp(phase.starOpacity, next.starOpacity, phaseT);

    return {
      gradient: `linear-gradient(180deg, ${c1}, ${c2})`,
      sunPos: { x: sunX, y: sunY },
      moonPos: { x: moonX, y: moonY },
      stars: { opacity: starOpacity },
      label: phase.label,
    };
  }, [dayFrac]);
}

export default function SkyCycle({ height = "100vh", fastForwardMs = 6000 }) {
  const containerRef = useRef(null);
  const [playFrac, setPlayFrac] = useState(0); // 0..1 during intro fast-forward
  const [introDone, setIntroDone] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Memoized star positions to avoid recalculation
  const starPositions = useMemo(() => 
    Array.from({ length: 120 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 55,
      size: Math.random() * 2 + 0.5,
      opacity: 0.7 + Math.random() * 0.3,
    })), []
  );

  // Memoized shooting star seeds
  const shootingStarSeeds = useMemo(() => 
    Array.from({ length: 3 }, (_, i) => i), []
  );

  const targetFrac = useMemo(() => getNowFraction(), []);

  // Check for reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReduceMotion(e.matches);
    setReduceMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Animate from 0 → targetFrac over fastForwardMs, then freeze at real-time fraction
  useEffect(() => {
    if (reduceMotion) {
      setPlayFrac(targetFrac);
      setIntroDone(true);
      return;
    }

    let raf = 0;
    const start = performance.now();

    const tick = (t) => {
      const elapsed = t - start;
      if (elapsed >= fastForwardMs) {
        setPlayFrac(targetFrac);
        setIntroDone(true);
        return;
      }
      // Ease in-out for nicer feel
      const e = elapsed / fastForwardMs;
      const ease = e < 0.5 ? 2 * e * e : 1 - Math.pow(-2 * e + 2, 2) / 2;
      setPlayFrac(targetFrac * ease);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fastForwardMs, targetFrac, reduceMotion]);

  // After intro, slowly drift in real time (ambient)
  useEffect(() => {
    if (!introDone || reduceMotion) return;
    let raf = 0;

    const tick = () => {
      const realtimeFrac = getNowFraction();
      setPlayFrac(realtimeFrac);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [introDone, reduceMotion]);

  const sky = useSkyState(playFrac);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height,
        overflow: "hidden",
        background: sky.gradient,
        transition: "background 600ms linear",
      }}
      className="min-h-screen"
    >
      {/* Stars (static + twinkle) - Performance optimized */}
      <Stars opacity={sky.stars.opacity} positions={starPositions} />
      <ShootingStars enabled={sky.stars.opacity > 0.4} seeds={shootingStarSeeds} />

      {/* Sun - Using transforms for better performance */}
      <Celestial
        type="sun"
        xPercent={sky.sunPos.x}
        yVh={40 + sky.sunPos.y}
        size={60}
        visible={isDay(playFrac)}
      />

      {/* Moon - Using transforms for better performance */}
      <Celestial
        type="moon"
        xPercent={sky.moonPos.x}
        yVh={40 + sky.moonPos.y}
        size={44}
        visible={!isDay(playFrac)}
      />

      {/* Horizon glow during sunrise/sunset */}
      <HorizonGlow fraction={playFrac} />
    </div>
  );
}

function isDay(frac) {
  const hour = fractionToHour(frac);
  return hour >= 6 && hour < 20; // simple rule
}

function Celestial({ type, xPercent, yVh, size = 48, visible = true }) {
  const common = {
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    opacity: visible ? 1 : 0,
    filter: visible ? "none" : "blur(2px)",
    pointerEvents: "none",
    transform: `translate(calc(${xPercent}vw - ${size / 2}px), ${yVh}vh)`,
    transition: "transform 300ms linear, filter 300ms linear, opacity 300ms linear",
    willChange: "transform, opacity",
  };

  if (type === "sun") {
    return (
      <div
        aria-hidden="true"
        role="presentation"
        style={{
          ...common,
          background:
            "radial-gradient(circle at 30% 30%, #fff7a5 0%, #ffd54f 45%, #ff9800 70%, rgba(255,152,0,0.0) 72%)",
          boxShadow:
            "0 0 24px 8px rgba(255,200,0,0.55), 0 0 80px 20px rgba(255,180,0,0.25)",
        }}
      />
    );
  }

  // moon
  return (
    <div
      aria-hidden="true"
      role="presentation"
      style={{
        ...common,
        background:
          "radial-gradient(circle at 30% 30%, #ffffff 0%, #e1e8f0 60%, #c9d2dc 85%)",
        boxShadow: "0 0 24px 8px rgba(200,220,255,0.35)",
      }}
    >
      {/* craters */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background:
            "radial-gradient(10px 10px at 60% 35%, rgba(0,0,0,0.08), rgba(0,0,0,0) 60%), radial-gradient(8px 8px at 40% 60%, rgba(0,0,0,0.06), rgba(0,0,0,0) 60%), radial-gradient(6px 6px at 65% 65%, rgba(0,0,0,0.06), rgba(0,0,0,0) 60%)",
          mixBlendMode: "multiply",
        }}
      />
    </div>
  );
}

function Stars({ opacity = 1, positions = [] }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !positions.length) return;

    // Generate optimized stars using cached positions and box-shadows
    const stars = positions.map(pos => 
      `${pos.x}vw ${pos.y}vh 0 ${pos.size}px rgba(255,255,255,${pos.opacity})`
    ).join(", ");
    el.style.boxShadow = stars;
  }, [positions]);

  return (
    <>
      <div
        aria-hidden="true"
        ref={ref}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          borderRadius: "50%",
          opacity,
          transition: "opacity 800ms ease-out",
          animation: "twinkle 2.2s infinite ease-in-out",
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      <style>{`
        @keyframes twinkle {
          0%, 100% { filter: brightness(0.9); }
          50% { filter: brightness(1.3); }
        }
      `}</style>
    </>
  );
}

function ShootingStars({ enabled = true, seeds = [] }) {
  if (!enabled) return null;
  return (
    <>
      {seeds.map((i) => (
        <ShootingStar key={i} delay={1000 + i * 3500} />
      ))}
    </>
  );
}

function ShootingStar({ delay = 1000 }) {
  const [restartKey, setRestartKey] = useState(0);
  
  // Memoized trajectory to avoid recalculation
  const trajectory = useMemo(() => ({
    startX: -10 + Math.random() * 40,
    startY: 5 + Math.random() * 25,
    dx: 40 + Math.random() * 30,
    dy: 20 + Math.random() * 20,
    dur: 1200 + Math.random() * 900,
  }), [restartKey]);

  useEffect(() => {
    const id = setInterval(() => setRestartKey((k) => k + 1), 10000 + Math.random() * 8000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      key={restartKey}
      aria-hidden="true"
      style={{
        position: "absolute",
        width: 2,
        height: 2,
        borderRadius: 2,
        background: "white",
        boxShadow: "0 0 10px 2px rgba(255,255,255,0.8)",
        transform: `translate(${trajectory.startX}vw, ${trajectory.startY}vh)`,
        animation: `shoot${restartKey} ${trajectory.dur}ms linear ${delay}ms both`,
        pointerEvents: "none",
        opacity: 0.9,
        willChange: "transform, opacity",
      }}
    >
      <style>{`
        @keyframes shoot${restartKey} {
          0%   { transform: translate(${trajectory.startX}vw, ${trajectory.startY}vh); opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translate(${trajectory.startX + trajectory.dx}vw, ${trajectory.startY + trajectory.dy}vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function HorizonGlow({ fraction }) {
  // Memoized glow calculation
  const glowOpacity = useMemo(() => {
    const hour = fractionToHour(fraction);
    const proximity = Math.min(
      Math.min(Math.abs(hour - 6), Math.abs(hour - 20)),
      24 - Math.max(Math.abs(hour - 6), Math.abs(hour - 20))
    );
    return clamp(1 - proximity / 2.5, 0, 1);
  }, [fraction]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: -100,
        height: 220,
        background:
          "radial-gradient(120% 120% at 50% 0%, rgba(255,200,120,0.45), rgba(255,120,0,0.1) 40%, rgba(0,0,0,0) 70%)",
        opacity: glowOpacity,
        transition: "opacity 300ms linear",
        pointerEvents: "none",
        willChange: "opacity",
      }}
    />
  );
}